import React, { ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { StyleSheetManager } from 'styled-components';

/**
 * Copied from https://github.com/Briggybros/styled-window-portal/blob/master/src/StyledWindowPortal.tsx
 *
 * Modified to optionally copy all styles from parent window and use the html5 doctype
 *
 */

interface IWindowProps {
	toolbar?: boolean | ((props: IWindowProps, window: Window) => boolean);
	location?: boolean | ((props: IWindowProps, window: Window) => boolean);
	directories?: boolean | ((props: IWindowProps, window: Window) => boolean);
	status?: boolean | ((props: IWindowProps, window: Window) => boolean);
	menubar?: boolean | ((props: IWindowProps, window: Window) => boolean);
	scrollbars?: boolean | ((props: IWindowProps, window: Window) => boolean);
	resizable?: boolean | ((props: IWindowProps, window: Window) => boolean);
	width?: number | ((props: IWindowProps, window: Window) => number);
	height?: number | ((props: IWindowProps, window: Window) => number);
	top?: number | ((props: IWindowProps, window: Window) => number);
	left?: number | ((props: IWindowProps, window: Window) => number);
}

interface IPopupWindowProps {
	onClose: ((this: WindowEventHandlers, ev: Event) => any) | null;
	autoClose: boolean;
	title?: string;
	windowProps?: IWindowProps;
	children: ReactNode;
}

interface IPopupWindowState {
	externalWindow: Window | null;
}

export default class PopupWindow extends React.PureComponent<IPopupWindowProps, IPopupWindowState> {

	private static defaultProps = {
		onClose: () => {},
		title: 'New Window',
		windowProps: {
			toolbar: false,
			location: false,
			directories: false,
			status: false,
			menubar: false,
			scrollbars: true,
			resizable: true,
			width: 500,
			height: 400,
			top: (props: IWindowProps, window: Window) =>
				window.screen.height / 2 - window.outerHeight / 2,
			left: (props: IWindowProps, window: Window) =>
				window.screen.width / 2 - window.outerWidth / 2,
		},
	};

	private container: HTMLElement;

	constructor(props: IPopupWindowProps) {
		super(props);
		this.state = {
			externalWindow: null,
		};
		this.container = document.createElement('div');
	}

	private createWindow(): Window {
		const w = window.open('', '', this.windowPropsToString());

		w.document.open();
		w.document.writeln("<!DOCTYPE html>");
		w.document.close();

		return w;
	}

	public componentDidMount() {
		this.setState(
			{
				externalWindow: this.createWindow(),
			},
			() => {
				if (this.state.externalWindow != null) {
					this.state.externalWindow.onunload = this.props.onClose;

					const title = this.state.externalWindow.document.createElement(
						'title'
					);

					title.innerText = !!this.props.title ? this.props.title : '';

					this.state.externalWindow.document.head.appendChild(title);
					this.state.externalWindow.document.body.appendChild(this.container);

					// Inject global style
					Array.from(document.head.getElementsByTagName('STYLE'))
						.filter(
							(style) =>
								(style as HTMLStyleElement).innerText.indexOf(
									'\n/!* sc-component-id: sc-global'
								) !== -1
						)
						.forEach((style) => {
							if (this.state.externalWindow != null) {
								this.state.externalWindow.document.head.appendChild(
									style.cloneNode(true)
								);
							}
						});

					// Inject linked styles
					Array.from(document.head.getElementsByTagName('LINK'))
						/*.filter(
							(style) =>
								(style as HTMLLinkElement).innerText.indexOf(
									'\n/!* sc-component-id: sc-global'
								) !== -1
						)*/
						.forEach((link) => {
							if (this.state.externalWindow != null) {
								this.state.externalWindow.document.head.appendChild(
									link.cloneNode(true)
								);
							}
						});
				}
			}
		);

		if (this.props.autoClose) {
			window.addEventListener("unload", this.closeExternalWindow);
		}
	}

	public componentDidUpdate(prevProps: IPopupWindowProps) {
		if (!prevProps.autoClose && this.props.autoClose) {
			// autoClose became enabled
			window.addEventListener("unload", this.closeExternalWindow);
		}

		if (prevProps.autoClose && !this.props.autoClose) {
			// autoClose became disabled
			window.removeEventListener("unload", this.closeExternalWindow);
		}
	}

	public componentWillUnmount() {
		if (this.props.autoClose) {
			window.removeEventListener("unload", this.closeExternalWindow);
		}

		this.closeExternalWindow();
	}

	public closeExternalWindow = () => {
		if (this.state.externalWindow && !this.state.externalWindow.closed) {
			this.state.externalWindow.close();
		}
	}

	private windowPropsToString() {
		const mergedProps: { [key: string]: any } = {
			...PopupWindow.defaultProps.windowProps,
			...this.props.windowProps,
		};

		return Object.keys(mergedProps)
			.map((key) => {
				switch (typeof mergedProps[key]) {
					case 'function':
						return `${key}=${mergedProps[key].call(this, mergedProps, window)}`;
					case 'boolean':
						return `${key}=${mergedProps[key] ? 'yes' : 'no'}`;
					default:
						return `${key}=${mergedProps[key]}`;
				}
			})
			.join(',');
	}

	public render() {
		if (!!this.state.externalWindow) {
			return (
				<StyleSheetManager target={this.state.externalWindow.document.head}>
					<div>
						{ReactDOM.createPortal(this.props.children, this.container)}
					</div>
				</StyleSheetManager>
			);
		} else {
			return null;
		}
	}
}



// WEBPACK FOOTER //
// ./src/core/components/editor/styled/PopupWindow.tsx